/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  Box,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableRow,
  Checkbox,
  IconButton,
} from "@mui/material";
import React, { useMemo, useState, useRef, useCallback } from "react";
import {
  StyledTableHeadCell,
  StyledTableHead,
  StyledTableBodyCell,
  StyledTypography,
  StyledTableRow,
  StyledSelect,
  StyledPerPageContainer,
  StyledPaginationContainer,
  ExportContainer,
} from "../index_table/styles";
import { StyledModalTableContainer, StyledCheckBox, StyledTableSortLabel } from "./styles";
import { useTranslation } from "react-i18next";
import DownloadIcon from '@mui/icons-material/Download';
import csvLoader from '../../assets/images/csv2.gif'
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import RadioButtonCheckedOutlinedIcon from '@mui/icons-material/RadioButtonCheckedOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

export const ModalTable = ({
  report = true,
  columns,
  data,
  type,
  itemsPerPage,
  setItemsPerPage,
  page,
  setPage,
  pageCount,
  checkboxes = false,
  emptyStateText = "No Data Exists",
  selectedIds = [],
  setSelectedIds,
  hideNavigation,
  searchLoading = false,
  hasSearchTerm = false,
  enableRowClick = false,
  handleOpen,
  setSelectedRow,
  showDownloadButton = false,
  disableDownloadButton = true,
  handleExportCsv,
  csvLoading,
  evenRow = false,
  styledCheckbox = false
}) => {
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  const { t } = useTranslation();
  const storageKey = `tableWidths-${type || "default"}`;

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) return JSON.parse(saved);
    return columns.reduce((acc, col) => {
      acc[col.key] = col.width || 150;
      return acc;
    }, {});
  });

  const resizingRef = useRef({ columnKey: null, startX: 0, startWidth: 0 });

  // Handles column resizing
  const startResizing = (key, e) => {
    e.preventDefault();
    resizingRef.current = { columnKey: key, startX: e.clientX, startWidth: columnWidths[key] };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
  };

  const handleMouseMove = useCallback((e) => {
    const { columnKey, startX, startWidth } = resizingRef.current;
    if (!columnKey) return;
    const newWidth = Math.max(80, startWidth + e.clientX - startX);
    setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
  }, []);

  const handleMouseUp = useCallback((e) => {
    const { columnKey, startX, startWidth } = resizingRef.current;
    if (!columnKey) return;

    const finalWidth = Math.max(80, startWidth + e.clientX - startX);

    localStorage.setItem(storageKey, JSON.stringify({ ...columnWidths, [columnKey]: finalWidth }));
    setColumnWidths((prev) => ({ ...prev, [columnKey]: finalWidth }));

    resizingRef.current = { columnKey: null, startX: 0, startWidth: 0 };
    document.body.style.cursor = "";

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [columnWidths, storageKey, handleMouseMove]);

  const handleRowSelect = (row) => {
    if (enableRowClick) {
      setSelectedRow(row);
      handleOpen();
    }
  };

  const handleSelect = (id) => {
    const selectedIndex = selectedIds.indexOf(id);
    const newSelected = selectedIndex === -1
      ? [...selectedIds, id]
      : [...selectedIds.slice(0, selectedIndex), ...selectedIds.slice(selectedIndex + 1)];
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.length === visibleRows?.length) setSelectedIds([]);
    else setSelectedIds(visibleRows?.map((row) => row.id));
  };

  const handleSort = (property) => {
    if (property.sortable === false) return;
    const isAsc = orderBy === property.key && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property.key);
  };

  const descendingComparator = (a, b, orderBy) => {
    const getSortValue = (value) => {
      if (value && typeof value === "object" && value.sortValue !== undefined) return value.sortValue;
      if (value && typeof value === "string" && value.sortValue !== undefined) return value.sortValue.toLowerCase();
      if (value && typeof value === "string") return value.toLowerCase();
      return value;
    };
    const aValue = getSortValue(a[orderBy]);
    const bValue = getSortValue(b[orderBy]);
    const isNumeric = (string) => /^[+-/$]?\d+(\.\d+)?$/.test(string);
    if (isNumeric(aValue) && isNumeric(bValue)) {
      if (aValue[0] === "$" && bValue[0] === "$") return Number(bValue.substring(1)) - Number(aValue.substring(1));
      return bValue - aValue;
    }
    if ((bValue || "") < (aValue || "")) return -1;
    if ((bValue || "") > (aValue || "")) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => (order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy));

  const visibleRows = useMemo(() => {
    if (!data?.length) return [];

    const totalRow = data.find(row => row.id === "__total__");
    const normalRows = data.filter(row => row.id !== "__total__");

    const sortedRows = [...normalRows].sort(getComparator(order, orderBy));

    return totalRow ? [totalRow, ...sortedRows] : sortedRows;
  }, [data, order, orderBy]);

  const totalCount = Math.ceil(pageCount / itemsPerPage);
  const from = totalCount === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const to = Math.min(page * itemsPerPage, pageCount);

  const isDiscountCardsPath =
    /^\/admin\/perq_cards\/?$/.test(location.pathname) ||
    /^\/admin\/perq_cards\/create\/?$/.test(location.pathname) ||
    /^\/admin\/perq_cards\/edit\/\d+\/?$/.test(location.pathname);

  const pulsingText = {
    textAlign: "center",
    animation: "pulse 1.5s ease-in-out infinite",
    "@keyframes pulse": { "0%": { opacity: 1 }, "50%": { opacity: 0.5 }, "100%": { opacity: 1 } },
  };

  return (
    <Box
      sx={{ marginRight: report ? "30px" : 0 }}
    >
      <StyledPerPageContainer>
        <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {!hideNavigation && (
            <>
              <StyledSelect
                value={itemsPerPage}
                size="small"
                onChange={(e) => {
                  setItemsPerPage(e.target.value);
                  setPage(1);
                }}
                data-testid="index-table-select"
              >
                {[10, 25, 50, 100].map((num) => (
                  <MenuItem key={num} value={num} sx={{ fontSize: "0.875rem" }} data-testid={`menu-item-${num}`}>
                    {num}
                  </MenuItem>
                ))}
              </StyledSelect>
              <StyledTypography variant="body2">
                {`${type} ${isDiscountCardsPath ? t("discount-cards.index.table.per-page") : "per page"}`}
              </StyledTypography>
            </>
          )}
        </Box>
        {!hideNavigation && (
          <ExportContainer>
            <StyledTypography variant="body2">
              {`${isDiscountCardsPath ? t("discount-cards.index.table.showing") : "Showing"} ${from} ${
                isDiscountCardsPath ? t("discount-cards.index.table.to") : "to"
              } ${to} ${isDiscountCardsPath ? t("discount-cards.index.table.of") : "of"} ${pageCount} ${type}`}
            </StyledTypography>
            {
              showDownloadButton && (
                csvLoading ? (
                  <IconButton disableRipple>
                    <img style={{ height: 24 }} src={csvLoader} alt="loading..." />
                  </IconButton>
                ) : (
                  <IconButton
                    disabled={disableDownloadButton}
                    onClick={handleExportCsv}
                    sx={{ color: '#2b4fb9' }}
                  >
                    <DownloadIcon />
                  </IconButton>
                )
              )
            }
          </ExportContainer>
        )}
      </StyledPerPageContainer>

      <StyledModalTableContainer>
        <Table>
          <StyledTableHead report={report} evenRow={evenRow}>
            <TableRow>
              {checkboxes && (
                <StyledTableHeadCell sx={{width: '10px'}}>
                  {
                    !data?.some(row => row.id === "__total__") && (
                      styledCheckbox ? (
                        <StyledCheckBox
                          icon={<CircleOutlinedIcon />}
                          checkedIcon={<RadioButtonCheckedOutlinedIcon />}
                          disableRipple
                          indeterminate={selectedIds.length > 0 && selectedIds.length < visibleRows?.length}
                          checked={visibleRows?.length > 0 && selectedIds.length === visibleRows?.length}
                          onChange={handleSelectAll}
                        />
                      ) : (
                        <Checkbox
                          indeterminate={selectedIds.length > 0 && selectedIds.length < visibleRows?.length}
                          checked={visibleRows?.length > 0 && selectedIds.length === visibleRows?.length}
                          onChange={handleSelectAll}
                        />
                      )
                    )
                  }
                </StyledTableHeadCell>
              )}
              {columns.map((col, index) => (
                <StyledTableHeadCell
                  key={index}
                  sortDirection={orderBy === col.key ? order : false}
                  sx={{ width: columnWidths[col.key], minWidth: 80, position: "relative", userSelect: "none" }}
                >
                  {col.sortable !== false ? (
                    <StyledTableSortLabel
                      active={orderBy === col.key}
                      direction={orderBy === col.key ? order : "asc"}
                      onClick={() => handleSort(col)}
                      hideSortIcon={false}
                      IconComponent={ orderBy !== col.key ? UnfoldMoreIcon : undefined }
                      orderBy={orderBy}
                      colKey={col.key}
                    >
                      {col.label}
                    </StyledTableSortLabel>
                  ) : (
                    <span style={{ cursor: "default" }}>{col.label}</span>
                  )}
                  <Box
                    onMouseDown={(e) => startResizing(col.key, e)}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      height: "100%",
                      width: "6px",
                      cursor: "col-resize",
                      zIndex: 10,
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
                    }}
                  />
                </StyledTableHeadCell>
              ))}
            </TableRow>
          </StyledTableHead>

          <TableBody>
            {visibleRows.length === 0 ? (
              <StyledTableRow report={report}>
                <StyledTableBodyCell colSpan={columns.length + (checkboxes ? 1 : 0)}>
                  {searchLoading && hasSearchTerm ? (
                    <StyledTypography variant="body2" sx={pulsingText}>
                      Searching...
                    </StyledTypography>
                  ) : (
                    <StyledTypography variant="body2">{emptyStateText}</StyledTypography>
                  )}
                </StyledTableBodyCell>
              </StyledTableRow>
            ) : (
              visibleRows?.map((item, index) => {
                const isAllRow = item.id === "__total__";
                const isAllChecked = selectedIds.length === 0;

                return (
                  <StyledTableRow
                    evenRow={evenRow}
                    report={report}
                    key={index}
                    hover={checkboxes}
                    selected={checkboxes && selectedIds.includes(item.id)}
                    onClick={() => handleRowSelect(item)}
                  >
                    {checkboxes && (
                      <StyledTableBodyCell report={report} sx={{width: '10px'}}>
                        {
                          styledCheckbox ? (
                            <StyledCheckBox
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<RadioButtonCheckedOutlinedIcon />}
                              disableRipple
                              checked={isAllRow ? isAllChecked : selectedIds.includes(item.id)}
                              onChange={() => {
                                if (item.id === "__total__") {
                                  handleSelectAll();
                                } else {
                                  handleSelect(item.id);
                                }
                              }}
                            />
                          ) : (
                            <Checkbox checked={selectedIds.includes(item.id)} onChange={() => handleSelect(item.id)} />
                          )
                        }
                      </StyledTableBodyCell>
                    )}
                    {columns.map((col, index) => (
                      <StyledTableBodyCell
                        report={report}
                        key={index}
                        sx={{ width: columnWidths[col.key], maxWidth: columnWidths[col.key], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {typeof item[col.key] === "object" && item[col.key]?.display !== undefined ? item[col.key].display : item[col.key]}
                      </StyledTableBodyCell>
                    ))}
                  </StyledTableRow>
              )})
            )}
          </TableBody>
        </Table>
      </StyledModalTableContainer>

      {!hideNavigation && (
        <StyledPaginationContainer>
          <Pagination page={page} count={totalCount} onChange={(_event, page) => setPage(page)} data-testid="index-table-pagination" />
        </StyledPaginationContainer>
      )}
    </Box>
  );
};
